import { useEffect, useRef, useState } from "react";
import type { ItineraryDay, ItineraryItem } from "@/lib/api/schemas";

// Maps URL APIs accept up to 9 intermediate waypoints (11 total stops
// including origin + destination). Trips with more stops get
// transport-item scaffolding stripped first; if still too long, we
// truncate to the head and surface a hint.
const MAX_STOPS = 11;

type TravelMode = "walking" | "driving" | "transit" | "bicycling";

interface Stop {
  lat: number;
  lng: number;
  isTransport: boolean;
}

interface DayDirectionsMenuProps {
  day: ItineraryDay;
}

// Per-day affordance on the DayCard header. Opens every mappable
// stop in the day as a multi-stop route in the user's chosen Maps
// app (Google or Apple) at their chosen travel mode. Default mode
// is walking — most travel days are city tourism.
export function DayDirectionsMenu({ day }: DayDirectionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<TravelMode>("walking");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const allStops = collectStops(day.items);
  let working = allStops;
  // If over the cap, drop transport items first — they're route
  // scaffolding ("Metro to Châtelet"), not destinations the
  // navigation app needs to stop at.
  if (working.length > MAX_STOPS) {
    const withoutTransport = allStops.filter((s) => !s.isTransport);
    working =
      withoutTransport.length <= MAX_STOPS ? withoutTransport : allStops;
  }
  const truncated = working.length > MAX_STOPS;
  const stops = truncated ? working.slice(0, MAX_STOPS) : working;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onOutside = (e: Event) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  if (stops.length === 0) return null;

  const launch = (provider: "google" | "apple") => {
    setOpen(false);
    const touch = isTouchDevice();
    const webURL =
      provider === "google"
        ? googleMapsWebURL(stops, mode)
        : appleMapsWebURL(stops, mode);
    if (!touch) {
      window.open(webURL, "_blank");
      return;
    }
    const appURL =
      provider === "google"
        ? googleMapsAppURL(stops, mode)
        : appleMapsAppURL(stops, mode);
    tryApp(appURL, webURL);
  };

  return (
    <div ref={wrapperRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Open ${stops.length} ${stops.length === 1 ? "stop" : "stops"} in Maps`}
        className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/5 px-2.5 py-0.5 text-[10px] font-semibold text-ink-light hover:text-ink hover:bg-white/10 transition-colors"
      >
        <span aria-hidden="true">🧭</span>
        <span>Open in Maps</span>
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label={`Open day ${day.dayNumber} in Maps`}
          className="absolute right-0 top-full mt-1.5 w-60 rounded-xl border border-white/15 bg-deep/95 shadow-2xl backdrop-blur-sm p-2 z-30"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-2 pt-1 pb-2 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-light/80">
              Travel mode
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <ModeRadio
                value="walking"
                current={mode}
                set={setMode}
                label="Walking"
              />
              <ModeRadio
                value="driving"
                current={mode}
                set={setMode}
                label="Driving"
              />
              <ModeRadio
                value="transit"
                current={mode}
                set={setMode}
                label="Transit"
              />
              <ModeRadio
                value="bicycling"
                current={mode}
                set={setMode}
                label="Cycling"
              />
            </div>
          </div>

          <div className="border-t border-white/10 mt-1 pt-1 space-y-0.5">
            <MenuLink
              icon="🗺️"
              label="Google Maps"
              onClick={() => launch("google")}
            />
            <MenuLink
              icon=""
              apple
              label="Apple Maps"
              onClick={() => launch("apple")}
            />
          </div>

          {truncated && (
            <p className="px-2 pt-1.5 pb-0.5 text-[10px] text-ink-light/80 leading-snug">
              Maps apps cap at {MAX_STOPS} stops. Opens the first
              {" "}{MAX_STOPS} of {allStops.length}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ModeRadio({
  value,
  current,
  set,
  label,
}: {
  value: TravelMode;
  current: TravelMode;
  set: (v: TravelMode) => void;
  label: string;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={(e) => {
        e.stopPropagation();
        set(value);
      }}
      className={`rounded-md px-2 py-1 text-left transition-colors ${
        active
          ? "bg-accent/20 text-accent-light border border-accent/40"
          : "text-ink-light hover:text-ink hover:bg-white/5 border border-transparent"
      }`}
    >
      {label}
    </button>
  );
}

function MenuLink({
  icon,
  apple,
  label,
  onClick,
}: {
  icon: string;
  apple?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left text-sm text-ink hover:bg-white/10 transition-colors"
    >
      <span aria-hidden="true" className="w-5 text-center text-base leading-none">
        {apple ? (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 inline-block" aria-hidden="true">
            <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.4 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.44zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.65-2.323-7.34 0-4.27 2.78-6.54 5.52-6.54 1.45 0 2.66.95 3.58.95.88 0 2.23-1.01 3.75-1.01.55 0 2.62.06 3.97 1.91-.35.22-2.44 1.44-2.44 3.86 0 2.43 2.12 3.53 2.43 3.68z" />
          </svg>
        ) : (
          icon
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}

// collectStops filters items to those with valid lat/lng. Keeps day
// order intact so the route follows the timeline.
function collectStops(items: ItineraryItem[] | undefined): Stop[] {
  if (!items) return [];
  const out: Stop[] = [];
  for (const it of items) {
    if (typeof it.lat !== "number" || typeof it.lng !== "number") continue;
    if (it.lat === 0 && it.lng === 0) continue;
    out.push({
      lat: it.lat,
      lng: it.lng,
      isTransport: it.type === "transport",
    });
  }
  return out;
}

// ---- URL builders ----

function googleMapsWebURL(stops: Stop[], mode: TravelMode): string {
  const params = new URLSearchParams({
    api: "1",
    travelmode: mode,
    origin: latLng(stops[0]),
    destination: latLng(stops.at(-1)!),
  });
  const waypoints = stops.slice(1, -1).map(latLng).join("|");
  if (waypoints) params.set("waypoints", waypoints);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function googleMapsAppURL(stops: Stop[], mode: TravelMode): string {
  const saddr = latLng(stops[0]);
  const rest = stops.slice(1).map(latLng).join("+to:");
  // Encode plus signs as %2B is unnecessary because comgooglemaps://
  // expects literal `+to:` separators between stops; URLSearchParams
  // would over-encode them, so we build the query manually.
  return `comgooglemaps://?saddr=${encodeURIComponent(saddr)}&daddr=${encodeURIComponent(rest)}&directionsmode=${mode}`;
}

function appleMapsWebURL(stops: Stop[], mode: TravelMode): string {
  const flg = appleFlag(mode);
  const saddr = latLng(stops[0]);
  const daddr = stops.slice(1).map(latLng).join("+to:");
  return `http://maps.apple.com/?saddr=${encodeURIComponent(saddr)}&daddr=${encodeURIComponent(daddr)}&dirflg=${flg}`;
}

function appleMapsAppURL(stops: Stop[], mode: TravelMode): string {
  const flg = appleFlag(mode);
  const saddr = latLng(stops[0]);
  const daddr = stops.slice(1).map(latLng).join("+to:");
  return `maps://?saddr=${encodeURIComponent(saddr)}&daddr=${encodeURIComponent(daddr)}&dirflg=${flg}`;
}

function appleFlag(mode: TravelMode): string {
  switch (mode) {
    case "walking":
      return "w";
    case "driving":
      return "d";
    case "transit":
      return "r";
    case "bicycling":
      return "c";
  }
}

function latLng(s: Stop): string {
  return `${s.lat},${s.lng}`;
}

// ---- Touch / device helpers (mirror DirectionsMenu pattern) ----

function isTouchDevice(): boolean {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || (navigator.maxTouchPoints ?? 0) > 0;
}

function tryApp(appUrl: string, fallbackUrl: string) {
  const started = Date.now();
  const onVisibility = () => {
    if (document.hidden) {
      document.removeEventListener("visibilitychange", onVisibility);
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.location.href = appUrl;
  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibility);
    if (!document.hidden && Date.now() - started < 1500) {
      window.open(fallbackUrl, "_blank");
    }
  }, 800);
}
